'use client'

import { Button } from '@/components/ui/button'
import {
  ArrowRight, ShieldCheck, Search, Bell, BrainCircuit, BarChart3, Sparkles,
  CheckCircle2, Zap, FileText, ListChecks, Map, Bot, Globe, GraduationCap,
  TrendingUp, Workflow, Target, Mail, Brain
} from 'lucide-react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface Feature {
  title: string
  items: string[]
}

interface CapabilitySection {
  icon: React.ElementType
  badge: string
  title: string
  description: string
  gradient: string
  color: string
  features: Feature[]
}

const capabilities: CapabilitySection[] = [
  {
    icon: Search,
    badge: 'Discovery',
    title: 'Opportunity Discovery & Matching',
    description: 'Access the largest aggregated database of government opportunities across every level—federal contracts, state and local bids, grants, and SBIR/STTR programs—with AI that surfaces your best-fit matches.',
    gradient: 'from-sky-500 to-blue-600',
    color: '#0ea5e9',
    features: [
      {
        title: 'Search & Filter',
        items: [
          'Federal, State, Local & Grants in one platform',
          'Advanced filters by agency, NAICS, set-aside, location',
          'Full-text search across millions of solicitations',
          'Save searches and create custom views',
        ],
      },
      {
        title: 'AI Matching',
        items: [
          'Proprietary scoring algorithm powered by your profile',
          'Semantic similarity + keyword + NAICS + location matching',
          'Certification-aware set-aside scoring',
          'Daily email notifications for high-value matches',
        ],
      },
    ],
  },
  {
    icon: FileText,
    badge: 'Proposals',
    title: 'AI Proposal Generation & Bid Extraction',
    description: 'Generate compliant, compelling proposals grounded in the actual solicitation documents. Extract every requirement automatically so nothing falls through the cracks.',
    gradient: 'from-violet-500 to-purple-600',
    color: '#8b5cf6',
    features: [
      {
        title: 'Proposal Generator',
        items: [
          'Multi-model AI (Gemini, OpenAI, Anthropic, Grok)',
          'Solicitation-aware generation grounded in RFP requirements',
          'Tone, length, and template customization',
          'Grounded in your company knowledge base and past performance',
        ],
      },
      {
        title: 'Bid Requirement Extraction',
        items: [
          'Auto-parse RFPs, SOWs, and amendments',
          'Structured checklist with priority tagging',
          'Source citations from original documents',
          'Tracks compliance across the bid lifecycle',
        ],
      },
    ],
  },
  {
    icon: BarChart3,
    badge: 'Management',
    title: 'Pipeline Management & Mapping',
    description: 'Visualize your entire bid pipeline from pursuit to award with interactive maps, geographic insights, and stage-by-stage tracking across every opportunity.',
    gradient: 'from-emerald-500 to-teal-600',
    color: '#10b981',
    features: [
      {
        title: 'Bid Pipeline',
        items: [
          'Stage-by-stage tracking (Tracking → Submitted → Awarded)',
          'KPI dashboard: Active, Due Soon, Submitted, Win Rate',
          'Auto-archive expired and orphaned opportunities',
          'Click-through navigation from pipeline charts to bids',
        ],
      },
      {
        title: 'Mapping & Visualization',
        items: [
          'Interactive geographic maps of opportunities',
          'Location-based filtering and clustering',
          'Visual pipeline charts with drill-down capability',
          'Custom bid notes, documents, and amendment tracking',
        ],
      },
    ],
  },
  {
    icon: Brain,
    badge: 'Intelligence',
    title: 'Market Intelligence & AI Research',
    description: 'Deploy AI agents on demand to research competitors, forecast awards, and uncover market trends—powered by our proprietary dataset of millions of historical and active government contract awards.',
    gradient: 'from-amber-500 to-orange-600',
    color: '#f59e0b',
    features: [
      {
        title: 'Research Agents',
        items: [
          'Deep-dive any agency, incumbent, or contract vehicle',
          'Synthesized reports with bid/no-bid recommendations',
          'Competitor win-rate analysis and teaming partner discovery',
          'Benchmark pricing against historical awards',
        ],
      },
      {
        title: 'Analytics & Insights',
        items: [
          'Daily intelligence briefs tailored to your profile',
          'AI-generated opportunity analysis and descriptions',
          'Market trend visualization and award forecasting',
          'Proprietary data combined with the latest online information',
        ],
      },
    ],
  },
  {
    icon: GraduationCap,
    badge: 'Coaching',
    title: 'AI Coaching & Strategy',
    description: 'Get a dedicated AI coach that analyzes your business profile, reviews your documents, and delivers personalized strategy recommendations to strengthen your competitive position.',
    gradient: 'from-rose-500 to-pink-600',
    color: '#f43f5e',
    features: [
      {
        title: 'Personalized Coaching',
        items: [
          'Business readiness assessments with scoring',
          'Document quality scoring and gap analysis',
          'Market positioning guidance and action plans',
          'Progress tracking and milestone monitoring',
        ],
      },
      {
        title: 'Smart Recommendations',
        items: [
          'AI-generated strategic recommendations',
          'Certification and capability gap identification',
          'Competitive intelligence and positioning advice',
          'Continuous improvement tracking over time',
        ],
      },
    ],
  },
  {
    icon: Bell,
    badge: 'Notifications',
    title: 'Real-Time Notifications & Alerts',
    description: 'Stay ahead of every deadline and opportunity with intelligent notifications that deliver the right information at the right time, across every channel.',
    gradient: 'from-blue-500 to-indigo-600',
    color: '#6366f1',
    features: [
      {
        title: 'Smart Alerts',
        items: [
          'Daily matched opportunity email digests',
          'Bid update and amendment detection',
          'Deadline reminders with countdown tracking',
          'Customizable notification preferences per category',
        ],
      },
      {
        title: 'AI Assistant',
        items: [
          'GovBot AI chatbot for instant answers',
          'RAG-powered responses grounded in your data',
          'Conversation history and context awareness',
          'Platform guidance and strategic Q&A',
        ],
      },
    ],
  },
]

const trustItems = [
  'Data Encryption at Rest & Transit',
  'Isolated Infrastructure',
  'Automated Backups',
  'SOC 2 Type II Ready',
  'GDPR Compliant',
  '99.9% Uptime SLA',
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-7xl mx-auto px-4 py-16 md:py-24">

        {/* Hero */}
        <motion.div
          className="text-center mb-14 md:mb-16"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Platform Capabilities</span>
          </motion.div>
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4 leading-tight"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
              Everything You Need
            </span>
            <br />
            <span className="text-foreground">to Win Government Contracts</span>
          </motion.h1>
          <motion.p variants={itemVariants} className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            From discovering your next opportunity to submitting a winning proposal—our AI-powered platform
            covers every stage of the government contracting lifecycle.
          </motion.p>
        </motion.div>

        {/* Capability Sections */}
        <div className="space-y-10 md:space-y-14 mb-14 md:mb-16">
          {capabilities.map((cap, sectionIdx) => {
            const Icon = cap.icon;
            const isReversed = sectionIdx % 2 === 1;
            return (
              <motion.div
                key={cap.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                variants={containerVariants}
              >
                <motion.div
                  variants={itemVariants}
                  className={cn(
                    "relative rounded-2xl border overflow-hidden",
                    "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
                  )}
                  style={{
                    boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
                  }}
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r" style={{
                    background: `linear-gradient(to right, ${cap.color}, ${cap.color}66)`,
                  }} />

                  <div className="p-5 md:p-8">
                    {/* Header */}
                    <div className={cn(
                      "flex flex-col md:flex-row md:items-center gap-3 mb-6",
                      isReversed && "md:flex-row-reverse md:text-right"
                    )}>
                      <div className={cn("p-3 rounded-xl bg-gradient-to-br w-fit", cap.gradient)}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2" style={isReversed ? { justifyContent: 'flex-end' } : undefined}>
                          <span className="text-xs font-bold tracking-widest uppercase" style={{ color: cap.color }}>
                            {cap.badge}
                          </span>
                          <div className="h-px w-12 bg-gradient-to-r" style={{
                            background: `linear-gradient(to right, ${cap.color}66, transparent)`,
                          }} />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-1.5">{cap.title}</h2>
                        <p className="text-muted-foreground max-w-2xl leading-relaxed" style={isReversed ? { marginLeft: 'auto' } : undefined}>
                          {cap.description}
                        </p>
                      </div>
                    </div>

                    {/* Feature Columns */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {cap.features.map((feature) => (
                        <div
                          key={feature.title}
                          className={cn(
                            "rounded-xl border p-4 transition-all duration-300",
                            "border-white/10 dark:border-white/5 bg-white/[0.03] dark:bg-white/[0.02]",
                            "hover:border-sky-500/20 hover:bg-sky-500/[0.03]"
                          )}
                        >
                          <h3 className="text-base font-semibold text-foreground mb-2.5">{feature.title}</h3>
                          <ul className="space-y-2">
                            {feature.items.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: cap.color }} />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust / Security */}
        <motion.div
          className="mb-14 md:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Enterprise-Grade Security</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
              Built on a Secure Foundation
            </h2>
            <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
              Our platform utilizes enterprise-grade infrastructure and adheres to strict security practices to protect your data.
            </p>
          </motion.div>
          <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-3">
            {trustItems.map((item) => (
              <div
                key={item}
                className={cn(
                  "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-300",
                  "backdrop-blur-xl border border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10",
                  "hover:border-emerald-500/30 hover:shadow-[0_0_20px_-5px_rgba(16,185,129,0.25)]"
                )}
                style={{
                  boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
                }}
              >
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <motion.div
            variants={itemVariants}
            className={cn(
              "relative rounded-2xl border p-6 md:p-10 text-center overflow-hidden",
              "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
            )}
            style={{
              boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 via-transparent to-blue-500/5 pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
                Ready to Start Winning?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
                Choose the plan that fits your needs and start leveraging AI-powered government contract intelligence today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="text-base px-8 h-12 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/30">
                  <Link href="/pricing">
                    View Pricing
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-base px-8 h-12 border-sky-500/30 hover:bg-sky-500/5">
                  <Link href="/contact">
                    Contact Sales
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>

      </div>
    </div>
  )
}
